import React, { useCallback, useEffect, useMemo, useState } from 'react';
import * as mentorApi from '../../api/mentorApi';

const GENERIC_ERROR = "Couldn't complete that - please try again.";

const AUDIENCES = [
  { id: 'session', label: 'Squad', help: 'Tags for the whole squad session.' },
  { id: 'chair', label: 'Chair', help: "Tags for the chair's evaluation." },
];

const KINDS = [
  { id: 'strength', label: 'Strengths' },
  { id: 'coaching', label: 'Coaching Required' },
];

function TagColumn({ audience, kind, tags, busyId, onAdd, onToggle, onMove }) {
  const [draft, setDraft] = useState('');
  const [hint, setHint] = useState('');
  const [error, setError] = useState('');

  const add = async (e) => {
    e.preventDefault();
    const label = draft.trim();
    if (!label) return;
    setError('');
    const problem = await onAdd(audience, kind, label, hint.trim());
    if (problem) setError(problem);
    else {
      setDraft('');
      setHint('');
    }
  };

  return (
    <div className="form-card-container esg-tags__div-1">
      <h4 className="esg-tags__h4-2">{KINDS.find((k) => k.id === kind).label}</h4>

      <form onSubmit={add} className="esg-tags__form-3">
        <input
          className="form-control-input esg-tags__input-4"
          placeholder="Add an option"
          aria-label={`Add ${KINDS.find((k) => k.id === kind).label} option`}
          maxLength={120}
          value={draft}
          onChange={(e) => { setError(''); setDraft(e.target.value); }}
        />
        <input
          className="form-control-input esg-tags__input-4"
          placeholder="Hover description (optional)"
          aria-label={`Description for the new ${KINDS.find((k) => k.id === kind).label} option`}
          maxLength={255}
          value={hint}
          onChange={(e) => { setError(''); setHint(e.target.value); }}
        />
        <button type="submit" className="esg-btn esg-btn-primary" disabled={draft.trim() === ''}>Add</button>
      </form>
      {error && <div className="rejection-banner esg-tags__div-5" role="alert">{error}</div>}

      <div className="esg-tags__div-6">
        {tags.length === 0 && <div className="no-student-placeholder">No options yet.</div>}
        {tags.map((t, i) => (
          <div
            key={t.id}
            style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 0', borderBottom: '1px solid var(--neutral-200, #e5e7eb)', opacity: t.is_active ? 1 : 0.55 }}
          >
            <span className="esg-tags__input-4">
              {t.label}
              {!t.is_active && <span className="role-badge revised esg-tags__span-7">INACTIVE</span>}
              {t.description && <div className="esg-tags__div-8">{t.description}</div>}
            </span>
            <span className="esg-tags__div-8" title="Times picked on evaluations">{t.times_used}×</span>
            <button type="button" className="esg-btn esg-btn-secondary" aria-label={`Move ${t.label} up`} disabled={i === 0 || busyId !== null} onClick={() => onMove(tags, i, -1)}>↑</button>
            <button type="button" className="esg-btn esg-btn-secondary" aria-label={`Move ${t.label} down`} disabled={i === tags.length - 1 || busyId !== null} onClick={() => onMove(tags, i, 1)}>↓</button>
            <button type="button" className="esg-btn esg-btn-secondary" disabled={busyId === t.id} onClick={() => onToggle(t)}>
              {t.is_active ? 'Deactivate' : 'Activate'}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

// Program Administrators manage the strengths / coaching-area options the ESG evaluation form offers. Names are
// add-only (never edited) so what past evaluations say stays true; an option is retired and a new one added.
export default function EsgTags({ onError, onSuccess }) {
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [audience, setAudience] = useState('session');
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    try {
      setTags(await mentorApi.getEsgTags());
    } catch (err) {
      onError?.("Couldn't load the options - try again.");
    } finally {
      setLoading(false);
    }
  }, [onError]);

  useEffect(() => {
    load();
  }, [load]);

  const grouped = useMemo(() => {
    const out = {};
    tags.forEach((t) => {
      const key = `${t.audience}:${t.kind}`;
      (out[key] = out[key] || []).push(t);
    });
    Object.values(out).forEach((list) => list.sort((a, b) => a.sort_order - b.sort_order || a.id - b.id));
    return out;
  }, [tags]);

  const replaceTag = (updated) => setTags((prev) => prev.map((t) => (t.id === updated.id ? { ...t, ...updated } : t)));

  // Returns a message when the add failed (shown under the field), otherwise undefined.
  const add = async (aud, kind, label, description) => {
    try {
      const created = await mentorApi.createEsgTag({ audience: aud, kind, label, description });
      setTags((prev) => [...prev, { times_used: 0, ...created }]);
      onSuccess?.('Option added.');
      return undefined;
    } catch (err) {
      const errors = err?.data?.errors;
      return err?.status === 422 ? (errors?.label?.[0] || errors?.kind?.[0] || GENERIC_ERROR) : GENERIC_ERROR;
    }
  };

  const toggle = async (tag) => {
    setBusyId(tag.id);
    try {
      replaceTag(await mentorApi.updateEsgTag(tag.id, { is_active: !tag.is_active }));
      onSuccess?.(tag.is_active ? 'Option set to inactive.' : 'Option set to active.');
    } catch (err) {
      onError?.(GENERIC_ERROR);
    } finally {
      setBusyId(null);
    }
  };

  // Swap positions with the neighbour by swapping their sort_order values.
  const move = async (list, index, direction) => {
    const a = list[index];
    const b = list[index + direction];
    if (!a || !b) return;
    setBusyId(a.id);
    try {
      const [ua, ub] = await Promise.all([
        mentorApi.updateEsgTag(a.id, { sort_order: b.sort_order }),
        mentorApi.updateEsgTag(b.id, { sort_order: a.sort_order }),
      ]);
      replaceTag(ua);
      replaceTag(ub);
    } catch (err) {
      onError?.(GENERIC_ERROR);
      load();
    } finally {
      setBusyId(null);
    }
  };

  const current = AUDIENCES.find((a) => a.id === audience);
  const kinds = KINDS;

  return (
    <div className="animate-fade-in">
      <div className="dashboard-title-row">
        <div>
          <h1 className="dashboard-title">ESG Options</h1>
          <p className="dashboard-subtitle">
            The strengths and coaching areas auditors pick from in ESG evaluations. Names can't be edited — set an option to inactive and add a new one so past evaluations stay accurate.
          </p>
        </div>
      </div>

      <div className="auditor-tab-row esg-tags__div-5">
        {AUDIENCES.map((a) => (
          <button key={a.id} type="button" className={`auditor-tab-button ${audience === a.id ? 'active' : ''}`} onClick={() => setAudience(a.id)}>
            {a.label}
          </button>
        ))}
      </div>
      <p className="esg-tags__p-9">{current.help}</p>

      {loading ? (
        <div className="placeholder-card">Loading options...</div>
      ) : (
        <div className="esg-tags__div-10">
          {kinds.map((k) => (
            <TagColumn
              key={`${audience}:${k.id}`}
              audience={audience}
              kind={k.id}
              tags={grouped[`${audience}:${k.id}`] || []}
              busyId={busyId}
              onAdd={add}
              onToggle={toggle}
              onMove={move}
            />
          ))}
        </div>
      )}
    </div>
  );
}
